import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import { KeyRound, CheckCircle2, Search } from 'lucide-react';

// What each permission module lets a user do, in plain words, grouped by the
// part of the system it belongs to. A module not listed here still shows
// (under "Other"), named from its code.
const CATALOG = {
  ManageLoans: { group: 'Loans', label: 'Manage loans', about: 'Full access to loans: capture, review, sign and process them.' },
  ViewLoans: { group: 'Loans', label: 'View loans', about: 'See loans and their details.' },
  EditLoan: { group: 'Loans', label: 'Edit loans', about: 'Change loan details and review applications.' },
  ApproveLoans: { group: 'Loans', label: 'Approve loans', about: 'Give final approval to a loan.' },
  OverrideAffordability: { group: 'Loans', label: 'Override the affordability check', about: 'Change the affordability check\'s Declined or Manual review decision on a loan, with a reason, so it can be approved. The statutory maximum instalment still applies.' },
  RejectLoans: { group: 'Loans', label: 'Reject loans', about: 'Turn a loan application down.' },
  CancelLoan: { group: 'Loans', label: 'Cancel loans', about: 'Cancel a loan that has not been paid out.' },
  RevertLoan: { group: 'Loans', label: 'Revert loans to Pending', about: 'Send a rejected loan back to Pending.' },
  TerminateContract: { group: 'Loans', label: 'Terminate contracts', about: 'End a loan contract early.' },
  reschedule_loan: { group: 'Loans', label: 'Reschedule loans', about: 'Change the repayment schedule of a loan.' },
  force_submit: { group: 'Loans', label: 'Submit without pre-qualification', about: 'Submit a loan even when the credit pre-qualification is missing.' },
  ExportLoans: { group: 'Loans', label: 'Export loans', about: 'Download loan lists (CSV).' },
  EnterRepayment: { group: 'Repayments', label: 'Enter repayments', about: 'Record payments made on loans.' },
  UndoRePayments: { group: 'Repayments', label: 'Undo repayments', about: 'Reverse a recorded repayment.' },
  RunOverdueProcessor: { group: 'Repayments', label: 'Run the overdue processor', about: 'Apply overdue status and penalties.' },
  ManageClients: { group: 'Clients', label: 'Manage clients', about: 'Add, edit and review clients and their documents.' },
  approve_client: { group: 'Clients', label: 'Approve clients', about: 'Activate client accounts that registered online.' },
  ViewProfile: { group: 'My account', label: 'View my profile', about: 'See and update your own client profile.' },
  SignDocuments: { group: 'My account', label: 'Apply and sign', about: 'Apply for loans and sign your loan agreements.' },
  GenerateReports: { group: 'Reports & compliance', label: 'Reports', about: 'Run and download reports, including NAMFISA reports.' },
  ManageCompliance: { group: 'Reports & compliance', label: 'Compliance cases', about: 'Handle consumer protection and compliance cases.' },
  AuditTrail: { group: 'Reports & compliance', label: 'Audit trail', about: 'See the record of who changed what.' },
  ManageConfig: { group: 'Administration', label: 'Company configuration', about: 'Company details, interest rates, loan expenses, roles and settings.' },
  ManageUsers: { group: 'Administration', label: 'Manage users', about: 'Add and manage staff user accounts.' },
  ManagePermissions: { group: 'Administration', label: 'Manage permissions', about: 'Change what each role may do.' },
  is_admin: { group: 'Administration', label: 'Administrator', about: 'Marks the role as an administrator.' },
  is_manager: { group: 'Administration', label: 'Manager', about: 'Marks the role as a manager.' },
  'Loan Reason': { group: 'Loan reasons', label: 'Loan reasons', about: 'Work with the list of loan reasons.' },
  'LoanReason.View': { group: 'Loan reasons', label: 'View loan reasons', about: 'See the list of loan reasons.' },
  'LoanReason.Create': { group: 'Loan reasons', label: 'Add loan reasons', about: 'Add new loan reasons.' },
  'LoanReason.Edit': { group: 'Loan reasons', label: 'Edit loan reasons', about: 'Change existing loan reasons.' },
  'LoanReason.Toggle': { group: 'Loan reasons', label: 'Turn loan reasons on or off', about: 'Enable or disable a loan reason.' },
};
const GROUP_ORDER = ['My account', 'Loans', 'Repayments', 'Clients', 'Reports & compliance', 'Loan reasons', 'Administration', 'Other'];

const describe = (module) => CATALOG[module] || {
  group: 'Other',
  label: String(module).replace(/[._]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase()),
  about: '',
};

const MyPermissions = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(apiUrl('/api/permissions/mine'), {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.error || (err.response?.status === 404
          ? 'Your permissions could not be loaded: the server needs updating (restart the API).'
          : 'Your permissions could not be loaded.'));
      }
    })();
  }, []);

  const groups = useMemo(() => {
    const rows = (data?.modules || [])
      .map((m) => ({ ...m, ...describe(m.module) }))
      .filter((m) => m.granted)
      .filter((m) => {
        const t = q.trim().toLowerCase();
        return !t || `${m.label} ${m.about} ${m.module} ${m.group}`.toLowerCase().includes(t);
      });
    const by = {};
    rows.forEach((m) => { (by[m.group] = by[m.group] || []).push(m); });
    return GROUP_ORDER.filter((g) => by[g]).map((g) => ({
      name: g,
      items: by[g].sort((a, b) => a.label.localeCompare(b.label)),
    }));
  }, [data, q]);

  const grantedCount = (data?.modules || []).filter((m) => m.granted).length;

  return (
    <div className="max-w-full">
      <div className="flex items-center gap-3 mb-1">
        <KeyRound className="text-blue-600 dark:text-blue-400" size={24} />
        <h1 className="text-2xl font-extrabold text-blue-800 dark:text-blue-300">My Permissions</h1>
      </div>
      <p className="text-sm text-gray-600 dark:text-slate-400 mb-4 max-w-3xl">
        What your role lets you do in the system. Permissions are given per role by an administrator — ask them if you
        need something you don't have.
      </p>

      {error && <div className="mb-4 p-3 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-sm">{error}</div>}
      {!data && !error && <p className="text-sm text-gray-500 dark:text-slate-400">Loading…</p>}

      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Your role</div>
              <div className="text-lg font-bold text-gray-900 dark:text-slate-100">{data.role || '—'}</div>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Permissions you have</div>
              <div className="text-lg font-bold text-emerald-700 dark:text-emerald-400">
                {data.allAccess ? 'Everything' : grantedCount}
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Areas</div>
              <div className="text-lg font-bold text-gray-900 dark:text-slate-100">
                {new Set((data.modules || []).filter((m) => m.granted).map((m) => describe(m.module).group)).size}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative flex-1 min-w-[12rem] max-w-sm">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search permissions"
                className="w-full !pl-8 !pr-3 !py-1.5 !text-xs" />
            </div>
          </div>

          {groups.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500 dark:text-slate-400">
              {q.trim() ? 'None of your permissions match.' : 'Your role has no permissions assigned yet.'}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
              {groups.map((g) => (
                <section key={g.name} className="rounded-xl border border-gray-200 dark:border-slate-700 border-t-4 border-t-blue-500 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
                  <header className="flex items-center justify-between px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-900/40">
                    <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100">
                      <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400">Module</span>
                      {g.name}
                    </h2>
                    <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400">{g.items.length}</span>
                  </header>
                  <ul className="divide-y divide-gray-100 dark:divide-slate-700">
                    {g.items.map((m) => (
                      <li key={m.module} className="flex items-start gap-2.5 px-3 py-2">
                        <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="text-xs font-semibold text-gray-900 dark:text-slate-100">{m.label}</span>
                            <code className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-[10px] font-mono text-slate-700 dark:text-slate-200">{m.module}</code>
                          </div>
                          {m.about && <div className="text-[11px] text-gray-500 dark:text-slate-400">{m.about}</div>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MyPermissions;
